/**
 * Shared checkbox and radio group renderers.
 * Used by both filters.ts and profile.ts with config-driven differences.
 */

export interface FormGroupOption {
  value: string;
  label: string;
}

export interface CheckboxGroupConfig {
  fieldId: string;
  options: FormGroupOption[];
  selectedValues: string[];
  labelText: string;
  /** Data attribute name for the checkbox input (e.g. 'data-filter-field' or 'data-field') */
  dataAttr: string;
  /** If true, wraps the whole group in a <div class="input-group"> */
  wrapInInputGroup?: boolean;
  /** If true, renders a PNTA checkbox via the provided pntaRenderer */
  supportsPnta?: boolean;
  /** Render function for the PNTA checkbox (only used when supportsPnta is true) */
  pntaRenderer?: (name: string, checked: boolean) => string;
}

export interface RadioGroupConfig {
  fieldId: string;
  options: FormGroupOption[];
  selectedValue: string | null;
  labelText: string;
  /** Data attribute name for the radio input */
  dataAttr: string;
  /** Additional data attribute (e.g. data-json-path) */
  extraDataAttr?: string;
  extraDataValue?: string;
  /** If true, wraps the whole group in a <div class="input-group"> */
  wrapInInputGroup?: boolean;
  /** If true, renders a PNTA checkbox via the provided pntaRenderer */
  supportsPnta?: boolean;
  /** Render function for the PNTA checkbox */
  pntaRenderer?: (name: string, checked: boolean) => string;
}

export function renderCheckboxGroup(config: CheckboxGroupConfig): string {
  const {
    fieldId, options, selectedValues, labelText, dataAttr,
    wrapInInputGroup = false, supportsPnta = false, pntaRenderer,
  } = config;

  const isPntaSelected = supportsPnta && selectedValues.includes('pnta');
  const visibleOptions = options.filter(o => o.value && o.value !== 'pnta');

  const checkboxes = visibleOptions.map(opt => {
    const isExclusive = opt.value === 'none';
    const isChecked = !isPntaSelected && selectedValues.includes(opt.value);
    return `
      <label class="checkbox-item">
        <input
          type="checkbox"
          name="${fieldId}"
          value="${opt.value}"
          ${isChecked ? 'checked' : ''}
          ${dataAttr}="${fieldId}"
          ${isExclusive ? 'data-exclusive="true"' : ''}
        />
        ${opt.label}
      </label>
    `;
  }).join('');

  const hadPnta = supportsPnta && options.some(o => o.value === 'pnta');
  const pntaHtml = hadPnta && pntaRenderer ? pntaRenderer(fieldId, isPntaSelected) : '';

  const inner = `
    <div class="label-with-indicator">
      <span class="group-label">${labelText}</span>
      <span class="save-indicator" id="indicator-${fieldId}">Saved <i class="fas fa-check"></i></span>
    </div>
    <div class="checkbox-group" id="${fieldId}">
      ${checkboxes}
      ${pntaHtml}
    </div>
  `;

  return wrapInInputGroup ? `<div class="input-group">${inner}</div>` : inner;
}

export function renderRadioGroup(config: RadioGroupConfig): string {
  const {
    fieldId, options, selectedValue, labelText, dataAttr,
    extraDataAttr, extraDataValue,
    wrapInInputGroup = false, supportsPnta = false, pntaRenderer,
  } = config;

  const selected = selectedValue?.toString() || '';
  const isPntaSelected = supportsPnta && selected === 'pnta';
  const visibleOptions = options.filter(o => o.value !== '' && o.value !== 'pnta');

  const radios = visibleOptions.map(opt => `
    <label class="radio-item">
      <input
        type="radio"
        name="${fieldId}"
        value="${opt.value}"
        ${!isPntaSelected && selected === opt.value ? 'checked' : ''}
        ${dataAttr}="${fieldId}"
        ${extraDataAttr && extraDataValue ? `${extraDataAttr}="${extraDataValue}"` : ''}
      />
      <span class="radio-circle"></span>
      ${opt.label}
    </label>
  `).join('');

  const hadPnta = supportsPnta && options.some(o => o.value === 'pnta');
  const pntaHtml = hadPnta && pntaRenderer ? pntaRenderer(fieldId, isPntaSelected) : '';

  const inner = `
    <div class="label-with-indicator">
      <span class="group-label">${labelText}</span>
      <span class="save-indicator" id="indicator-${fieldId}">Saved <i class="fas fa-check"></i></span>
    </div>
    <div class="radio-group" id="${fieldId}">
      ${radios}
    </div>
    ${pntaHtml}
  `;

  return wrapInInputGroup ? `<div class="input-group">${inner}</div>` : inner;
}
